import { IconButton } from '@material-ui/core';
import { Duvida as DuvidaAssessor } from 'api/Assessor/duvidas';
import { EditIcon, ReplyIcon } from 'components/Icons';
import { RefreshIcon } from 'components/Icons/RefreshIcon';
import LayoutContext from 'components/Layout/LayoutContext';
import { getDateFullTime } from 'helpers/utils/DateUtils';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import translation from 'locales/translation';
import MensagensContext from 'pages/Assessor/MensagensAssessor/Context';
import {
  AlertModal,
  ModalManager,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  assembleiasAssessorActionsVisibilidadeDuvida,
  parametroAssessorResultadoPadrao,
} from 'store/modules/AssembleiasAssessorActions';
import { IReducer } from 'store/Reducer';
import { toggleResponse } from '.';
import ChatContext from './ChatContext';
import FlagIsPuplic from './components/FlagIsPublic';
import './styles.scss';

const rootClassName = 'duvida-assessor';

interface Props extends DuvidaAssessor {
  isAdmin?: boolean;
}

export default function Duvida(props: Props) {
  const dispatch = useDispatch();
  const { conversaAtual, auth, atualizarMensagens } =
    useContext(MensagensContext);
  const { duvidaAtual, setDuvida, setRespostaPadraoResponse } =
    useContext(ChatContext);

  const { usuario } = useSelector((state: IReducer) => state.auth);

  const { showToast } = useContext(LayoutContext);

  const [loading, setLoading] = useState(false);
  const { evento } = useSelector(
    (state: IReducer) => state.assembleiasAssessor
  );

  const isCurrent = useMemo(() => {
    if (duvidaAtual?.idDuvidaFrequente === props.idDuvidaFrequente) {
      return true;
    }

    return false;
  }, [duvidaAtual, props.idDuvidaFrequente]);

  const toggleStatus = useCallback(
    async (flgPublica: boolean) => {
      dispatch(
        assembleiasAssessorActionsVisibilidadeDuvida(
          props.idDuvidaFrequente,
          flgPublica,
          () => {},
          (fail) => {
            ErrorHandler(fail);
          }
        )
      );
    },
    [props, dispatch]
  );

  const ModalManagerRef = useRef<any>();
  const openModal = (component: any, props: any) => {
    ModalManagerRef?.current.openModal(component, props);
  };
  const closeModal = (mk: any) => {
    ModalManagerRef?.current.closeModal(mk);
  };

  const responder = useMemo(() => {
    return (
      (usuario?.perfil.id === 'pertencer_consulta' &&
        !auth?.responder &&
        evento?.tipo === 'REU') ||
      auth?.responder
    );
  }, [auth?.responder, evento?.tipo, usuario?.perfil.id]);

  return (
    <div className={rootClassName}>
      <ModalManager ref={ModalManagerRef} />
      <p className={`${rootClassName}-pergunta-data`}>
        {getDateFullTime(new Date(props.dataDuvida))} - {conversaAtual!.nome}
      </p>
      <div
        className={
          !props.resposta && responder
            ? `${rootClassName}-container-pergunta w100`
            : `${rootClassName}-container-pergunta`
        }
      >
        <div className={`${rootClassName}-container-responder`}>
          <div className={`${rootClassName}-pergunta-container`}>
            <div className={`${rootClassName}-pergunta`}>
              <p className={`${rootClassName}-pergunta-texto`}>
                {props.duvida}
              </p>
            </div>
            {!props.resposta && props.isAdmin && (
              <FlagIsPuplic
                setPublic={() => toggleStatus(true)}
                setPrivate={() => toggleStatus(false)}
                disabledPublic={props.flgPublica}
                disabledPrivate={!props.flgPublica}
              />
            )}
          </div>
          {!props.resposta && responder && !props.isAdmin && (
            <IconButton
              className={`${rootClassName}-button-responder`}
              disabled={isCurrent || loading}
              disableRipple
              onClick={() => {
                setLoading(true);
                dispatch(
                  parametroAssessorResultadoPadrao(
                    evento!.cooperativa,
                    (response: any) => {
                      setRespostaPadraoResponse(response);
                      toggleResponse(
                        {
                          idDuvida: props!?.idDuvidaFrequente,
                          flgEmEdicao: Boolean(props!?.resposta),
                          flgEmResposta: true,
                        },
                        () => {
                          setLoading(false);
                          setDuvida(props);
                        },
                        (err) => {
                          setLoading(false);
                          let title = translation.ChatModal.aviso.title;
                          let children =
                            translation.ChatModal.aviso.description;

                          if (err!?.response!?.status! === 409) {
                            children =
                              translation.ChatModal.aviso.jaFoiRespondida;
                          }
                          const mKey = `alert${getUniqueKey()}`;
                          openModal(AlertModal, {
                            preventMaskExit: true,
                            size: 'small',
                            modalKey: mKey,
                            title,
                            children,
                            language: {
                              confirm:
                                err!?.response!?.status! === 409
                                  ? translation.ChatModal.aviso.atualizar
                                  : translation.ChatModal.aviso.confirmar,
                            },
                            onClose: () => {
                              closeModal(mKey);
                            },
                            onConfirm: () => {
                              closeModal(mKey);
                              if (err!?.response!?.status! === 409) {
                                atualizarMensagens();
                              }
                            },
                          });
                        }
                      );
                    },
                    () => {
                      setLoading(false);
                      showToast({
                        pauseOnFocusLoss: true,
                        theme: ToastTypes.Error,
                        showStatusBar: true,
                        timeout: 4000,
                        label: translation.AssessorMensagens['erro-parametro'],
                      });
                    }
                  )
                );
              }}
            >
              {loading ? <RefreshIcon /> : <ReplyIcon />}
            </IconButton>
          )}

          {!props.resposta && responder && props.isAdmin && (
            <div className={`${rootClassName}-container-buttons`}>
              <IconButton
                className={`${rootClassName}-button edit`}
                onClick={() => {
                  setLoading(true);
                  dispatch(
                    parametroAssessorResultadoPadrao(
                      evento!.cooperativa,
                      (response: any) => {
                        setRespostaPadraoResponse(response);
                        toggleResponse(
                          {
                            idDuvida: props!?.idDuvidaFrequente,
                            flgEmEdicao: Boolean(props!?.resposta),
                            flgEmResposta: true,
                          },
                          () => {
                            setLoading(false);
                            setDuvida(props);
                          },
                          (err) => {
                            setLoading(false);
                            let title = translation.ChatModal.aviso.title;
                            let children =
                              translation.ChatModal.aviso.description;

                            if (err!?.response!?.status! === 409) {
                              children =
                                translation.ChatModal.aviso.jaFoiRespondida;
                            }
                            const mKey = `alert${getUniqueKey()}`;
                            openModal(AlertModal, {
                              preventMaskExit: true,
                              size: 'small',
                              modalKey: mKey,
                              title,
                              children,
                              language: {
                                confirm:
                                  err!?.response!?.status! === 409
                                    ? translation.ChatModal.aviso.atualizar
                                    : translation.ChatModal.aviso.confirmar,
                              },
                              onClose: () => {
                                closeModal(mKey);
                              },
                              onConfirm: () => {
                                closeModal(mKey);
                                if (err!?.response!?.status! === 409) {
                                  atualizarMensagens();
                                }
                              },
                            });
                          }
                        );
                      },
                      () => {
                        setLoading(false);
                        showToast({
                          pauseOnFocusLoss: true,
                          theme: ToastTypes.Error,
                          showStatusBar: true,
                          timeout: 4000,
                          label:
                            translation.AssessorMensagens['erro-parametro'],
                        });
                      }
                    )
                  );
                }}
                disabled={isCurrent}
              >
                {loading ? <RefreshIcon /> : <EditIcon />}
              </IconButton>
            </div>
          )}
        </div>
      </div>

      {props.resposta && props.dataResposta && (
        <>
          <p className={`${rootClassName}-resposta-data`}>
            {getDateFullTime(new Date(props.dataResposta))} -{' '}
            {props.usuarioResposta}
          </p>
          <div className={`${rootClassName}-container-resposta`}>
            <div className={`${rootClassName}-resposta`}>
              <p className={`${rootClassName}-resposta-texto`}>
                {props.resposta}
              </p>
            </div>
            <div>
              {responder && (
                <div className={`${rootClassName}-container-buttons`}>
                  <IconButton
                    className={`${rootClassName}-button edit`}
                    onClick={() => {
                      setLoading(true);
                      dispatch(
                        parametroAssessorResultadoPadrao(
                          evento!.cooperativa,
                          (response: any) => {
                            setRespostaPadraoResponse(response);
                            toggleResponse(
                              {
                                idDuvida: props!?.idDuvidaFrequente,
                                flgEmEdicao: Boolean(props!?.resposta),
                                flgEmResposta: true,
                              },
                              () => {
                                setLoading(false);
                                setDuvida(props);
                              },
                              (err) => {
                                setLoading(false);
                                let title = translation.ChatModal.aviso.title;
                                let children =
                                  translation.ChatModal.aviso.description;

                                if (err!?.response!?.status! === 409) {
                                  children =
                                    translation.ChatModal.aviso.jaFoiRespondida;
                                }
                                const mKey = `alert${getUniqueKey()}`;
                                openModal(AlertModal, {
                                  preventMaskExit: true,
                                  size: 'small',
                                  modalKey: mKey,
                                  title,
                                  children,
                                  language: {
                                    confirm:
                                      err!?.response!?.status! === 409
                                        ? translation.ChatModal.aviso.atualizar
                                        : translation.ChatModal.aviso.confirmar,
                                  },
                                  onClose: () => {
                                    closeModal(mKey);
                                  },
                                  onConfirm: () => {
                                    closeModal(mKey);
                                    if (err!?.response!?.status! === 409) {
                                      atualizarMensagens();
                                    }
                                  },
                                });
                              }
                            );
                          },
                          () => {
                            setLoading(false);
                            showToast({
                              pauseOnFocusLoss: true,
                              theme: ToastTypes.Error,
                              showStatusBar: true,
                              timeout: 4000,
                              label:
                                translation.AssessorMensagens['erro-parametro'],
                            });
                          }
                        )
                      );
                    }}
                    disabled={isCurrent}
                  >
                    {loading ? <RefreshIcon /> : <EditIcon />}
                  </IconButton>
                </div>
              )}
            </div>
          </div>
          <div className={`${rootClassName}-container-flg`}>
            {props.resposta &&
              ((usuario?.perfil.id === 'pertencer_consulta' &&
                !auth?.alterarSituacao &&
                evento?.tipo === 'REU') ||
                auth?.alterarSituacao) && (
                <FlagIsPuplic
                  setPublic={() => toggleStatus(true)}
                  setPrivate={() => toggleStatus(false)}
                  disabledPublic={props.flgPublica}
                  disabledPrivate={!props.flgPublica}
                />
              )}
          </div>
        </>
      )}
    </div>
  );
}
